import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  Button,
} from '@heroui/react';
import { Check, Sparkles, X } from 'lucide-react';
import { useEntitlements } from '@/contexts/EntitlementsContext';
import { useAuth } from '@/contexts/AuthContext';
import { billingApi, BillingInterval, FeatureKey, PlanSlug, SubscriptionPlan } from '@/services/billingApi';
import { getPlanHighlights } from './BillingPlans';

export interface UpgradePlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  feature?: FeatureKey;
  title?: string;
  description?: string;
  requiredPlan?: string;
  priceText?: string;
  actionLabel?: string;
}

const PERKS = [
  '14-day Money Back Guarantee',
  'Cancel Anytime',
  'Cloud & Offline Sync',
  'Secure Payments via Stripe',
];

export const UpgradePlanModal: React.FC<UpgradePlanModalProps> = ({
  isOpen,
  onClose,
  feature,
  title,
  description,
  requiredPlan,
  actionLabel,
}) => {
  const { entitlements, plan } = useEntitlements();
  const { user } = useAuth();
  const isOwner = user?.role === 'OWNER';

  const [interval, setInterval] = useState<BillingInterval>('MONTHLY');
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [loadingPlans, setLoadingPlans] = useState<boolean>(true);
  const [loadingSlug, setLoadingSlug] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    let mounted = true;
    const fetchPlans = async () => {
      try {
        setLoadingPlans(true);
        const data = await billingApi.listPlans();
        if (mounted && data && data.length > 0) {
          setPlans(data);
        }
      } catch (err) {
      } finally {
        if (mounted) setLoadingPlans(false);
      }
    };

    void fetchPlans();
    return () => {
      mounted = false;
    };
  }, [isOpen]);

  const sub = entitlements?.subscription;
  const currentPlanSlug = plan?.slug || sub?.plan?.slug || null;
  // Treat as active paid subscriber only if status is ACTIVE and not restricted
  const isPaidActive = sub?.status === 'ACTIVE';

  const handleSelectPlan = async (targetPlan: SubscriptionPlan) => {
    if (!isOwner) {
      setErrorMsg('Only the account owner can upgrade or change subscription plans.');
      return;
    }

    try {
      setLoadingSlug(targetPlan.slug);
      setErrorMsg(null);
      const res = await billingApi.createCheckout({
        planSlug: targetPlan.slug as PlanSlug,
        interval,
      });

      if (res?.url) {
        window.location.href = res.url;
      }
    } catch (err: any) {
      setErrorMsg(err.message || 'Unable to open Stripe checkout. Please try again.');
      setLoadingSlug(null);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="5xl"
      placement="center"
      scrollBehavior="inside"
      classNames={{
        base: 'max-w-5xl max-h-[90vh] rounded-3xl bg-white shadow-2xl border border-gray-100',
        header: 'p-0 border-none',
        body: 'p-0 overflow-y-auto',
        closeButton: 'top-6 right-6 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-full p-2 transition-all z-20',
      }}
    >
      <ModalContent>
        {() => (
          <>
            <ModalHeader className="px-6 sm:px-10 pt-8 pb-2 flex flex-col text-left">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
                {title || 'Upgrade Plan'}
              </h2>
              {description && (
                <p className="text-xs sm:text-sm text-gray-500 font-medium mt-1">
                  {description}
                </p>
              )}
            </ModalHeader>

            <ModalBody className="px-6 sm:px-10 pb-10">
              {/* Error Message */}
              {errorMsg && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-2xl text-xs flex items-center justify-between">
                  <span>{errorMsg}</span>
                  <button
                    type="button"
                    onClick={() => setErrorMsg(null)}
                    className="font-bold ml-2 text-red-500 hover:text-red-700"
                  >
                    <X size={14} />
                  </button>
                </div>
              )}

              {/* Monthly / Yearly Toggle Switch */}
              <div className="flex justify-center my-4 sm:my-6">
                <div className="inline-flex items-center p-1 bg-gray-100 rounded-full border border-gray-200">
                  <button
                    type="button"
                    onClick={() => setInterval('MONTHLY')}
                    className={`px-5 py-2 text-xs font-semibold rounded-full transition-all ${
                      interval === 'MONTHLY'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-500 hover:text-gray-900'
                    }`}
                  >
                    Monthly
                  </button>
                  <button
                    type="button"
                    onClick={() => setInterval('YEARLY')}
                    className={`px-5 py-2 text-xs font-semibold rounded-full transition-all flex items-center gap-1.5 ${
                      interval === 'YEARLY'
                        ? 'bg-white text-gray-900 shadow-sm'
                        : 'text-gray-500 hover:text-gray-900'
                    }`}
                  >
                    <span>Yearly</span>
                    <span className="text-emerald-600 font-bold text-[11px]">
                      (Save 20%)
                    </span>
                  </button>
                </div>
              </div>

              {/* 3 Plan Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 items-stretch">
                {plans.map((p) => {
                  const isCurrent = Boolean(isPaidActive && currentPlanSlug && currentPlanSlug === p.slug);
                  const isPopular = p.slug === 'shop_plus';
                  const isRequired = Boolean(requiredPlan && (
                    p.name.toLowerCase().includes(requiredPlan.toLowerCase()) ||
                    p.slug.toLowerCase().includes(requiredPlan.toLowerCase())
                  ));
                  const isProcessing = loadingSlug === p.slug;
                  const priceMonthly = Number(p.priceMonthly);
                  const priceYearly =
                    p.priceYearly !== undefined && p.priceYearly !== null
                      ? Number(p.priceYearly)
                      : priceMonthly * 12;
                  const displayPrice = interval === 'YEARLY' ? priceYearly : priceMonthly;
                  const highlights = getPlanHighlights(p);

                  return (
                    <div
                      key={p.slug}
                      className={`relative flex flex-col rounded-2xl border p-6 sm:p-7 transition-all bg-white ${
                        isRequired
                          ? 'border-amber-500 ring-2 ring-amber-500/20 shadow-md'
                          : isPopular
                          ? 'border-emerald-500 ring-2 ring-emerald-500/20 shadow-md'
                          : 'border-gray-200 shadow-sm hover:border-gray-300'
                      }`}
                    >
                      {/* Required / Popular Badge */}
                      {isRequired ? (
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3.5 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-300 text-[11px] font-bold flex items-center gap-1 shadow-xs">
                          <Sparkles size={12} className="text-amber-600" />
                          <span>Required for this Feature</span>
                        </div>
                      ) : isPopular ? (
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-300 text-[11px] font-semibold flex items-center gap-1 shadow-xs">
                          <span>Most Popular</span>
                        </div>
                      ) : null}

                      {/* Plan Name & Description */}
                      <div className="min-h-[58px]">
                        <h3 className="text-lg font-bold text-gray-900">{p.name}</h3>
                        <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                          {p.description}
                        </p>
                      </div>

                      {/* Price */}
                      <div className="my-5 flex items-baseline gap-1.5">
                        <span className="text-4xl font-extrabold text-gray-900">
                          £{displayPrice}
                        </span>
                        <div className="flex flex-col text-[11px] text-gray-400 font-medium leading-tight">
                          <span>per {interval === 'YEARLY' ? 'year' : 'month'}</span>
                          <span>billed {interval === 'YEARLY' ? 'annually' : 'monthly'}</span>
                        </div>
                      </div>

                      {/* CTA Button - Fully rounded */}
                      <div className="mb-6">
                        {isCurrent ? (
                          <Button
                            isDisabled
                            className="w-full h-11 rounded-full text-xs font-semibold bg-gray-100 text-gray-400 cursor-default"
                          >
                            Current Plan
                          </Button>
                        ) : (
                          <Button
                            onPress={() => handleSelectPlan(p)}
                            isLoading={isProcessing}
                            className={`w-full h-11 rounded-full text-xs font-semibold transition-all shadow-xs ${
                              isPopular
                                ? 'bg-emerald-500 hover:bg-emerald-600 text-white'
                                : 'bg-white hover:bg-gray-50 text-gray-900 border border-gray-300'
                            }`}
                          >
                            {isProcessing ? (
                              'Checkout...'
                            ) : (
                              `Choose ${p.name}`
                            )}
                          </Button>
                        )}
                      </div>

                      {/* Feature Checklist */}
                      <div className="flex flex-col gap-2.5 pt-4 border-t border-gray-100 flex-1">
                        {highlights.map((feature, idx) => (
                          <div key={idx} className="flex items-start gap-2 text-xs text-gray-700">
                            <Check
                              size={14}
                              className="text-emerald-500 shrink-0 mt-0.5 stroke-[2.5]"
                            />
                            <span className="leading-snug">{feature}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Bottom Footer: ALL PLANS INCLUDE */}
              <div className="mt-8 pt-6 border-t border-gray-100 flex flex-col items-center gap-3 text-center">
                <span className="text-xs text-gray-400 uppercase">
                  All Plans Include:
                </span>
                <div className="flex flex-wrap items-center justify-center gap-2">
                  {PERKS.map((perk, idx) => (
                    <span
                      key={idx}
                      className="px-3.5 py-1 bg-gray-100 text-gray-600 rounded-full text-xs border border-gray-200"
                    >
                      {perk}
                    </span>
                  ))}
                </div>
              </div>
            </ModalBody>
          </>
        )}
      </ModalContent>
    </Modal>
  );
};

export default UpgradePlanModal;
